import React, { useEffect, useState } from 'react';
import { Button, IconButton } from '@material-ui/core';
import Assignment from '@material-ui/icons/Assignment';
import { useSelector } from 'react-redux';
import { IFile } from '../../api-client/autogenerated';
import { TooltipIfDisabledComponent } from '../custom-components/CustomButtons';
import {
  isFileRagScanComplete,
  useFileRagScanStatus,
} from '../custom-components/useFileRagScanStatus';
import { getProjectId } from '../../features/project/selectors';
import { getMeetingMinutesTaskSummary } from '../../models/api/meeting-minutes-tasks';

interface Props {
  file: IFile;
  isPhoneScreen?: boolean;
  className?: string;
  onClick: () => void;
  /**
   * True immediately after the review dialog is opened this session, so the label flips without
   * waiting for a refetch. Persistence across reloads comes from the extraction summary below.
   */
  hasOpened?: boolean;
}

const DISABLED_TOOLTIP =
  'This file is still being ingested. Please wait until processing finishes before extracting tasks.';

/**
 * Opens the Design Meeting Minutes task review dialog for the document this file
 * belongs to. Disabled — with an explanatory tooltip — until the file's
 * ragScanStatus reaches SUCCESS (its transcript text has finished being
 * extracted and is ready to be read by the extraction pipeline).
 *
 * The label is "View Unpublished Tasks" whenever the document already has unpublished draft
 * tasks (read from the extraction summary, so the transition survives reloads), otherwise
 * "Extract Tasks". `hasOpened` only makes the flip immediate within the current session.
 */
const ExtractTasksButton: React.FC<Props> = ({
  file,
  isPhoneScreen,
  className,
  onClick,
  hasOpened,
}) => {
  const ragScanStatus = useFileRagScanStatus(file);
  const disabled = !isFileRagScanComplete(ragScanStatus);

  const projectId = useSelector(getProjectId);
  const documentId = file.documentId;
  const [hasUnpublishedTasks, setHasUnpublishedTasks] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!projectId || !documentId) return;
    getMeetingMinutesTaskSummary(projectId, documentId)
      .then((summary) => {
        if (!cancelled) setHasUnpublishedTasks(summary.draftCount > 0);
      })
      .catch(() => {
        // A missing summary just leaves the label as "Extract Tasks".
      });
    return () => {
      cancelled = true;
    };
    // Re-check when reopening (e.g. after publishing/discarding in the dialog).
  }, [projectId, documentId, hasOpened]);

  const label = hasOpened || hasUnpublishedTasks ? 'View Unpublished Tasks' : 'Extract Tasks';

  return (
    <TooltipIfDisabledComponent disabled={disabled} title={disabled ? DISABLED_TOOLTIP : ''}>
      {isPhoneScreen ? (
        <IconButton aria-label={label} color="primary" onClick={onClick} disabled={disabled}>
          <Assignment />
        </IconButton>
      ) : (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<Assignment />}
          onClick={onClick}
          disabled={disabled}
          className={className}
        >
          {label}
        </Button>
      )}
    </TooltipIfDisabledComponent>
  );
};

export default ExtractTasksButton;
